import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Common metadata fields recognized by the logger.
 */
export interface LogMetadataFields {
  component?: string;
  module?: string;
  userId?: string;
  correlationId?: string;
  timestamp?: string;
}

/**
 * Metadata that can be attached to log entries.
 * Accepts LogMetadataFields or any object for flexibility in logging arbitrary data.
 * Note: We use 'object' to allow any non-primitive including class instances.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type LogMetadata = LogMetadataFields & { [key: string]: unknown } | object;

/**
 * Structured log entry for external logging services
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  metadata?: LogMetadata;
  args?: unknown[];
  stackTrace?: string;
}

/**
 * Configuration for the logger service
 */
export interface LoggerConfig {
  /** Minimum log level to output (e.g., 'warn' will show warn and error only) */
  minLevel?: LogLevel;
  /** Enable console output */
  enableConsole?: boolean;
  /** Enable structured logging (JSON format) */
  enableStructuredLogging?: boolean;
  /** External logging handler (e.g., for sending to monitoring service) */
  externalHandler?: (entry: LogEntry) => void;
  /** Default metadata to include in all logs */
  defaultMetadata?: LogMetadata;
}

/**
 * Production-safe logger service with advanced features
 *
 * Features:
 * - Automatic log level suppression in production
 * - Timestamp and context tracking
 * - Structured logging support
 * - External service integration capability
 * - Tree-shakeable implementation
 *
 * Usage:
 * ```typescript
 * constructor(private logger: LoggerService) {}
 *
 * // Simple logging
 * this.logger.error('Failed to load data', { error });
 *
 * // With context
 * this.logger.withContext({ component: 'ConfigEditor' })
 *   .error('Validation failed', { errors });
 *
 * // With metadata
 * this.logger.error('API error', { endpoint: '/api/config' }, error);
 * ```
 *
 * Production build configuration (angular.json + terser.config.json)
 * will strip debug/info calls via dead-code elimination.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly isProduction = environment.production;
  private readonly environmentMinLevel: LogLevel = this.resolveMinLevel();
  private config: LoggerConfig = {
    minLevel: this.environmentMinLevel,
    enableConsole: environment.logging.enableConsoleLog ?? !this.isProduction,
    enableStructuredLogging: this.isProduction,
    defaultMetadata: {}
  };
  private context: LogMetadata = {};

  private readonly levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
  };

  /**
   * Configure the logger service.
   * In production, security-relevant settings (minLevel, enableConsole) cannot be
   * lowered below environment defaults to prevent information leakage via XSS.
   */
  configure(config: Partial<LoggerConfig>): void {
    const safeConfig = this.isProduction ? this.enforceProductionLimits(config) : config;
    this.config = { ...this.config, ...safeConfig };
  }

  /**
   * Prevent runtime reconfiguration from lowering security settings in production.
   */
  private enforceProductionLimits(config: Partial<LoggerConfig>): Partial<LoggerConfig> {
    const result = { ...config };

    if (result.minLevel !== undefined &&
        this.levelPriority[result.minLevel] < this.levelPriority[this.environmentMinLevel]) {
      delete result.minLevel;
    }

    if (result.enableConsole === true) {
      delete result.enableConsole;
    }

    if (result.enableStructuredLogging === false) {
      delete result.enableStructuredLogging;
    }

    // Prevent replacing an existing external handler (e.g., Sentry) to block
    // XSS-based data exfiltration via a malicious handler.
    if (result.externalHandler !== undefined && this.config.externalHandler !== undefined) {
      delete result.externalHandler;
    }

    return result;
  }

  /**
   * Create a new logger instance with additional context.
   * Returns a new instance that snapshots the parent's config at creation time.
   * Subsequent configure() calls on the parent will NOT propagate to child loggers.
   *
   * @example
   * const componentLogger = this.logger.withContext({ component: 'MyComponent' });
   * componentLogger.error('Something went wrong'); // Includes component context
   */
  withContext(metadata: LogMetadata): LoggerService {
    const logger = Object.create(LoggerService.prototype);
    logger.isProduction = this.isProduction;
    logger.environmentMinLevel = this.environmentMinLevel;
    logger.config = { ...this.config };
    logger.context = { ...this.context, ...metadata };
    logger.levelPriority = this.levelPriority;
    return logger;
  }

  /**
   * Set global context that will be included in all logs
   */
  setGlobalContext(metadata: LogMetadata): void {
    this.config.defaultMetadata = { ...this.config.defaultMetadata, ...metadata };
  }

  /**
   * Debug level logging - only visible in development
   */
  debug(message: string, metadata?: LogMetadata, ...args: unknown[]): void {
    this.log('debug', message, metadata, ...args);
  }

  /**
   * Info level logging - only visible in development
   */
  info(message: string, metadata?: LogMetadata, ...args: unknown[]): void {
    this.log('info', message, metadata, ...args);
  }

  /**
   * Warning level logging - visible in all environments
   */
  warn(message: string, metadata?: LogMetadata, ...args: unknown[]): void {
    this.log('warn', message, metadata, ...args);
  }

  /**
   * Error level logging - visible in all environments
   * Includes stack trace capture
   */
  error(message: string, metadata?: LogMetadata, ...args: unknown[]): void {
    this.log('error', message, metadata, ...args);
  }

  /**
   * Core logging method with level-based filtering
   */
  private log(level: LogLevel, message: string, metadata?: LogMetadata, ...args: unknown[]): void {
    // Check if log level meets minimum threshold
    if (!this.shouldLog(level)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const combinedMetadata = {
      ...this.config.defaultMetadata,
      ...this.context,
      ...metadata,
      timestamp
    };

    const entry: LogEntry = {
      level,
      message,
      timestamp,
      metadata: combinedMetadata,
      args
    };

    // Capture stack trace for errors
    if (level === 'error') {
      entry.stackTrace = new Error().stack;
    }

    // Console output
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }

    // External handler (e.g., for APM, error tracking services)
    if (this.config.externalHandler) {
      try {
        this.config.externalHandler(entry);
      } catch (err) {
        // Prevent logging errors from breaking the application.
        // In production, suppress error details to avoid information leakage.
        if (this.isProduction) {
          console.error('Logger external handler failed');
        } else {
          console.error('Logger external handler failed:', err);
        }
      }
    }
  }

  /**
   * Check if log level meets minimum threshold
   */
  private shouldLog(level: LogLevel): boolean {
    const minLevel = this.config.minLevel || 'debug';
    return this.levelPriority[level] >= this.levelPriority[minLevel];
  }

  /**
   * Resolve the minimum log level from environment configuration.
   * Falls back to production='warn' / development='debug' if env value is invalid.
   */
  private resolveMinLevel(): LogLevel {
    const level = environment.logging.level;
    if (level === 'debug' || level === 'info' || level === 'warn' || level === 'error') {
      return level;
    }
    return this.isProduction ? 'warn' : 'debug';
  }

  /**
   * Output log entry to console
   */
  private logToConsole(entry: LogEntry): void {
    // Defense-in-depth: enforce minLevel even if caller doesn't check
    if (!this.shouldLog(entry.level)) {
      return;
    }

    const { level, message, metadata, args } = entry;

    if (this.config.enableStructuredLogging) {
      // Structured JSON format for production
      const structuredLog = {
        level,
        message,
        ...metadata,
        data: args && args.length > 0 ? args : undefined
      };
      console[level](this.safeStringify(structuredLog));
    } else {
      // Developer-friendly format
      const prefix = this.formatPrefix(level, metadata);
      const logArgs = args && args.length > 0 ? args : [];

      switch (level) {
        case 'debug':
          console.debug(prefix, message, ...logArgs);
          break;
        case 'info':
          console.info(prefix, message, ...logArgs);
          break;
        case 'warn':
          console.warn(prefix, message, ...logArgs);
          break;
        case 'error':
          console.error(prefix, message, ...logArgs);
          if (entry.stackTrace) {
            console.error('Stack trace:', entry.stackTrace);
          }
          break;
      }
    }
  }

  /**
   * Safely stringify data that may contain circular references.
   */
  private safeStringify(value: unknown): string {
    const seen = new WeakSet<object>();
    return JSON.stringify(value, (_key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) {
          return '[Circular]';
        }
        seen.add(val);
      }
      return val;
    });
  }

  /**
   * Format log prefix with level, timestamp, and context
   */
  private formatPrefix(level: LogLevel, metadata?: LogMetadata): string {
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });

    const levelStr = level.toUpperCase().padEnd(5);
    const contextParts: string[] = [];

    // Safely access metadata properties which may or may not exist
    const metaObj = metadata as Record<string, unknown> | undefined;
    if (metaObj?.['component'] && typeof metaObj['component'] === 'string') {
      contextParts.push(metaObj['component']);
    }
    if (metaObj?.['module'] && typeof metaObj['module'] === 'string') {
      contextParts.push(metaObj['module']);
    }

    const context = contextParts.length > 0 ? ` [${contextParts.join('/')}]` : '';

    return `[${timestamp}] ${levelStr}${context}`;
  }

  /**
   * Create a performance measurement logger
   * Returns a function that logs elapsed time when called
   *
   * @example
   * const timer = this.logger.time('API Call');
   * await fetchData();
   * timer(); // Logs: "API Call completed in 123ms"
   */
  time(label: string, metadata?: LogMetadata): () => void {
    const startTime = performance.now();
    return () => {
      const elapsed = Math.round(performance.now() - startTime);
      this.info(`${label} completed in ${elapsed}ms`, metadata);
    };
  }

  /**
   * Log a group of related messages.
   * Only renders console group when debug-level logging is enabled (minLevel <= 'debug')
   * and console output is active. The callback always executes regardless of log level.
   *
   * @example
   * this.logger.group('Configuration Validation', () => {
   *   this.logger.debug('Step 1: Check required fields');
   *   this.logger.debug('Step 2: Validate types');
   * });
   */
  group(label: string, callback: () => void): void {
    if (this.shouldLog('debug') && this.config.enableConsole) {
      console.group(label);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    } else {
      callback();
    }
  }

  /**
   * Log table data for debugging.
   * Only renders when debug-level logging is enabled (minLevel <= 'debug')
   * and console output is active.
   */
  table(data: Record<string, unknown>[], metadata?: LogMetadata): void {
    if (this.shouldLog('debug') && this.config.enableConsole) {
      const prefix = this.formatPrefix('debug', metadata);
      console.log(prefix, 'Table data:');
      console.table(data);
    }
  }
}
